//import vue
import { ref, onUnmounted, onMounted } from "vue";
import type { Ref } from "vue";

/**
 * @function useVisibleElement - tracks if an element is in viewport
 * @param {Ref<null|HTMLElement>}element - element to track
 * @returns {Ref<Boolean>} if the element is in the viewport
 */
export default function useVisibleElement(
  element: Ref<null | HTMLElement>
): Ref<boolean> {
  /** @const {Ref<Boolean>} isVisibleElement - reactive reference that tells if the element is visible */
  const isVisibleElement = ref(false);

  /** @function doIsVisible - calculates the bounding client rectangle and change is visible element flag according to the result */
  function isVisible(): void {
    //exit if the window or the element doesn't exist
    //also allow us to type non undefined the element
    if (!element || !window) {
      isVisibleElement.value = false;
      return;
    }
    //calculate the bounding rectangle
    const rect = (element as Ref<HTMLElement>).value?.getBoundingClientRect();
    const height = (element as Ref<HTMLElement>).value.offsetHeight;
    const width = (element as Ref<HTMLElement>).value.offsetWidth;
    //set the flag value according to this logic
    const visible =
      rect.top >= -height &&
      rect.left >= -width &&
      rect.right <=
        (window.innerWidth || document.documentElement.clientWidth) + width &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) + height;
    //change the value
    isVisibleElement.value = visible;
  }

  onMounted(() => {
    window.addEventListener("load", isVisible);
    window.addEventListener("scroll", isVisible);
  });
  onUnmounted(() => {
    window.removeEventListener("load", isVisible);
    window.removeEventListener("scroll", isVisible);
  });

  return isVisibleElement;
}
